<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import LucideIcon from 'dashboard/components/LucideIcon.vue';
import { useMapGetter } from 'dashboard/composables/store';

const props = defineProps({
  agent: {
    type: Object,
    required: true,
  },
  showEditAction: {
    type: Function,
    required: true,
  },
  showDeleteAction: {
    type: Function,
    required: true,
  },
  loading: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['edit', 'delete']);
const { t } = useI18n();
const isRTL = useMapGetter('accounts/isRTL');

const getAgentRoleName = (agent) => {
  if (!agent.custom_role_id) {
    return t(`AGENT_MGMT.AGENT_TYPES.${agent.role.toUpperCase()}`);
  }
  // For custom roles, you might need to pass customRoles as prop or get from store
  return agent.custom_role_name || agent.role;
};

const statusConf = computed(() => {
  const value = (props.agent?.availability_status || '').toLowerCase();
  const map = {
    online: {
      text: 'Online',
      cls: 'text-green-600 bg-[rgba(18,183,106,0.08)]',
      dot: 'bg-green-500',
    },
    offline: {
      text: 'Offline',
      cls: 'text-gray-500 bg-[rgba(37,43,55,0.08)]',
      dot: 'bg-gray-400',
    },
  };
  return map[value] || { text: value || '—', cls: 'text-gray-500 bg-transparent', dot: 'bg-transparent' };
});

function onEdit() {
  emit('edit', props.agent);
}

function onDelete() {
  emit('delete', props.agent);
}
</script>

<template>
  <div
    class="rounded-lg border border-n-gray-2 bg-n-gray-1 dark:border-n-vars-black-bd/80 dark:bg-n-vars-black-br/30 p-3 w-full text-n-gray-3"
    :class="{ 'text-right': isRTL }"
  >
    <!-- Top Section: Avatar, Name, Email & Actions -->
    <div class="flex items-center justify-between mb-3">
      <div class="flex items-center gap-[6px]">
        <!-- Avatar -->
        <div class="relative flex-shrink-0 w-10 h-10">
          <Thumbnail
            :src="agent.thumbnail"
            :username="agent.name"
            size="40px"
          />
        </div>

        <!-- Name & Email -->
        <div class="min-w-0">
          <h6 class="block font-semibold text-sm text-n-gray-7 dark:text-n-gray-3 capitalize truncate">
            {{ agent.name }}
          </h6>
          <span class="text-xs text-n-gray-5 truncate block">
            {{ agent.email }}
          </span>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex gap-1">
        <woot-button
          v-if="showEditAction(agent)"
          variant="smooth"
          size="small"
          icon="edit"
          class-names="!bg-n-gray-25 !text-n-gray-5 !border-n-gray-1 dark:!border-n-vars-black-bd/80 dark:!bg-n-vars-black-bd/80 dark:!text-n-gray-3 !rounded-lg !w-8 !h-8 !p-0"
          @click="onEdit"
        />
        <woot-button
          v-if="showDeleteAction(agent)"
          variant="smooth"
          size="small"
          icon="dismiss-circle"
          class-names="!bg-n-gray-25 !text-n-gray-5 !border-n-gray-1 dark:!border-n-vars-black-bd/80 dark:!bg-n-vars-black-bd/80 dark:!text-n-gray-3 !rounded-lg !w-8 !h-8 !p-0"
          :is-loading="loading[agent.id]"
          @click="onDelete"
        />
      </div>
    </div>

    <!-- Middle Section: Verification & Role -->
    <div class="flex justify-between items-center mb-3">
      <div class="text-sm font-medium text-n-gray-6">
        <span v-if="!agent.confirmed">{{ $t('AGENT_MGMT.LIST.VERIFICATION_PENDING') }}</span>
        <span v-else>{{ $t('AGENT_MGMT.LIST.VERIFIED') }}</span>
      </div>
      <div class="text-sm font-semibold text-n-gray-6 dark:text-n-gray-3">
        {{ getAgentRoleName(agent) }}
      </div>
    </div>

    <!-- Bottom Section: Status -->
    <div class="flex justify-between items-center">
      <div class="text-sm font-medium text-n-gray-5">Trạng thái</div>
      <div :class="`px-2 py-1 rounded text-xs font-medium ${statusConf.cls}`">
        {{ statusConf.text }}
      </div>
    </div>
  </div>
</template>
